import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, useMotionValue } from "framer-motion";
import { ArrowLeftRight } from "lucide-react";

interface BeforeAfterSliderProps {
    beforeImage: string;
    afterImage: string;
    beforeLabel?: string;
    afterLabel?: string;
}

export function BeforeAfterSlider({
    beforeImage,
    afterImage,
    beforeLabel = "Antes",
    afterLabel = "Depois",
}: BeforeAfterSliderProps) {
    const [sliderPosition, setSliderPosition] = useState(50);
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleMove = useCallback(
        (event: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent) => {
            if (!isDragging || !containerRef.current) return;

            const { left, width } = containerRef.current.getBoundingClientRect();
            let clientX;

            if ("touches" in event) {
                clientX = event.touches[0].clientX;
            } else {
                clientX = (event as MouseEvent).clientX;
            }

            const position = ((clientX - left) / width) * 100;
            setSliderPosition(Math.min(100, Math.max(0, position)));
        },
        [isDragging]
    );

    const handleMouseDown = useCallback(() => setIsDragging(true), []);
    const handleMouseUp = useCallback(() => setIsDragging(false), []);

    useEffect(() => {
        document.addEventListener("mousemove", handleMove);
        document.addEventListener("mouseup", handleMouseUp);
        document.addEventListener("touchmove", handleMove);
        document.addEventListener("touchend", handleMouseUp);

        return () => {
            document.removeEventListener("mousemove", handleMove);
            document.removeEventListener("mouseup", handleMouseUp);
            document.removeEventListener("touchmove", handleMove);
            document.removeEventListener("touchend", handleMouseUp);
        };
    }, [handleMove, handleMouseUp]);

    return (
        <div className="w-full relative select-none group rounded-2xl overflow-hidden border border-white/10 shadow-glass">
            <div
                ref={containerRef}
                className="relative w-full aspect-video overflow-hidden cursor-ew-resize"
                onMouseDown={handleMouseDown}
                onTouchStart={handleMouseDown}
            >
                {/* Images */}
                <img
                    src={afterImage}
                    alt={afterLabel}
                    className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                />
                <div
                    className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none"
                    style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
                >
                    <img
                        src={beforeImage}
                        alt={beforeLabel}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                </div>

                {/* Slider Handle */}
                <div
                    className="absolute inset-y-0 w-1 bg-white/50 backdrop-blur-sm cursor-col-resize z-20 pointer-events-none"
                    style={{ left: `${sliderPosition}%` }}
                >
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg text-black/70">
                        <ArrowLeftRight className="w-5 h-5" />
                    </div>
                </div>

                {/* Labels */}
                <div className="absolute top-4 left-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm pointer-events-none z-10">
                    {beforeLabel}
                </div>
                <div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm pointer-events-none z-10">
                    {afterLabel}
                </div>
            </div>
        </div>
    );
}
